/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { assistantsUpgradeIdentity } from "../funcs/assistantsUpgradeIdentity.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Assistant } from "../models/components/assistant.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpgradeAssistantIdentityRequest,
  UpgradeAssistantIdentitySecurity,
} from "../models/operations/upgradeassistantidentity.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AssistantsUpgradeIdentityMutationVariables = {
  request: UpgradeAssistantIdentityRequest;
  security?: UpgradeAssistantIdentitySecurity | undefined;
  options?: RequestOptions;
};

export type AssistantsUpgradeIdentityMutationData = Assistant;

export type AssistantsUpgradeIdentityMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * upgradeAssistantIdentity assistants
 *
 * @remarks
 * Give an existing assistant an agent and per-trigger workload identities. By default a new agent is created with access to every MCP server and skill in the project and to administering this assistant. Passing agent_id points the assistant at an existing agent of the project instead; that agent keeps its policy and gains administration of this assistant. Either way the agent is managed like any other agent afterwards. Existing assistants are never upgraded implicitly; repeating the upgrade is safe.
 */
export function useAssistantsUpgradeIdentityMutation(
  options?: MutationHookOptions<
    AssistantsUpgradeIdentityMutationData,
    AssistantsUpgradeIdentityMutationError,
    AssistantsUpgradeIdentityMutationVariables
  >,
): UseMutationResult<
  AssistantsUpgradeIdentityMutationData,
  AssistantsUpgradeIdentityMutationError,
  AssistantsUpgradeIdentityMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAssistantsUpgradeIdentityMutation(client, options),
    ...options,
  });
}

export function mutationKeyAssistantsUpgradeIdentity(): MutationKey {
  return ["@gram/client", "assistants", "upgradeIdentity"];
}

export function buildAssistantsUpgradeIdentityMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AssistantsUpgradeIdentityMutationVariables,
  ) => Promise<AssistantsUpgradeIdentityMutationData>;
} {
  return {
    mutationKey: mutationKeyAssistantsUpgradeIdentity(),
    mutationFn: function assistantsUpgradeIdentityMutationFn({
      request,
      security,
      options,
    }): Promise<AssistantsUpgradeIdentityMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(assistantsUpgradeIdentity(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
